import { AddCircle, CalendarViewMonth } from '@mui/icons-material'
import { Box, CircularProgress, Typography } from '@mui/material'
import { BlueButton } from '../../../../../customComponents/buttons/BlueButton'
import { useEffect, useState } from 'react'
import { type ProfessionalDevelopmentResource } from '../../../../../../models/ProfessionalDevelopmentResource'
import { BasicTable, type BasicTableColumn } from '../../BasicTable'
import { ProfessionalDevelopmentResourceModal } from './ProfessionalDevelopmentResourceModal'
import { type ThingsToConsiderSection } from '../../ThingsToConsiderCard'
import { ThingsToConsiderButton } from '../../ThingsToConsiderButton'
import { useModalState } from '../../../../../../hooks/use-modalState'
import { PriorityOneActivityName } from '../../../../../../models/PriorityOneActivityName'
import { DeleteItemModal } from '../../../DeleteItemModal'
import { TrackProfessionalResourceDevelopmentResourceModal } from './TrackProfessionalDevelopmentResourceModal'
import { SubBasicTable } from '../../SubBasicTable'
import { ClearTrackingModal } from '../../../../implementation/ClearTrackingModal'
import { ClearProfessionalDevelopmentResourceTracking, DeleteProfessionalDevelopmentResource, GetProfessionalDevelopmentResources } from '../../../../../../services/ProfessionalDevelopmentResourceService'

interface Props {
  priorityId: number
  readOnly: boolean
  trackingReadonly: boolean
  showTableOnly?: boolean
  hideExpand?: boolean
  implementationPhase?: boolean
  budgetOverviewView?: boolean
}

type ProfessionalDevelopmentResourceRow = ProfessionalDevelopmentResource & { displayBudget: string, expandedContent?: JSX.Element }

export const ResourcesList = (props: Props): JSX.Element => {
  const { handleCloseModal, modalState, startModal, currentItem } = useModalState<ProfessionalDevelopmentResource>(PriorityOneActivityName.OtherProfessionalDevelopmentResources)
  const [resources, setResources] = useState<ProfessionalDevelopmentResource[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [rows, setRows] = useState<ProfessionalDevelopmentResourceRow[]>([])

  useEffect(() => {
    void loadResources()
  }, [])

  useEffect(() => {
    setRows(resources.map(r => ({
      ...r,
      displayBudget: r.adjustedAmount != null ? `$${r.adjustedAmount}` : `$${r.budgetRequest}`,
      expandedContent: expandedContent(r)
    })))
  }, [resources])

  const expandedContent = (resource: ProfessionalDevelopmentResource): JSX.Element | undefined => {
    if (resource.utilized == null) {
      return undefined
    }

    const subTableRows: [{ id: number, utilized: string, actualAmount?: string }] = [{ id: resource.id, utilized: String(resource.utilized).toUpperCase() === 'TRUE' ? 'YES' : 'NO', actualAmount: `$${resource.actualAmount ?? 0}` }]

    return <Box
      sx={{
        p: '2rem',
        backgroundColor: 'var(--grey-100)'
      }}
    >
      <SubBasicTable
        hasTrackingDetails
        readonly={props.readOnly}
        trackingReadonly={props.trackingReadonly}
        onEditTracking={async () => { await startModal('track', resource) }}
        onDeleteTracking={async () => { await startModal('clearTrack', resource) }}
        columns={[
          { id: 'utilized', label: 'UTILIZED', minWidth: 200, isTrackingColumn: true },
          { id: 'actualAmount', label: 'ACTUAL DOLLAR AMOUNT', minWidth: 200, isTrackingColumn: true },
          { id: 'trackingAction', label: '', isTrackingColumn: true }
        ]}
        rows={subTableRows}
        dataTestId={`${resource.id}-expandedFocusBuildingTable`}
      />
    </Box>
  }

  const loadResources = async (): Promise<void> => {
    setIsLoading(true)
    await handleCloseModal()

    const resources = await GetProfessionalDevelopmentResources(props.priorityId, props.implementationPhase ?? false)
    setResources(resources)

    setIsLoading(false)
  }

  const columns: BasicTableColumn[] = [
    { id: 'resourceNeeded', label: 'Resource', minWidth: 400 },
    { id: 'resourcePurpose', label: 'Purpose', minWidth: 250 },
    { id: 'displayBudget', label: 'Budget', minWidth: 100 }
  ]

  const thingsToConsiderSections: ThingsToConsiderSection[] = [
    {
      title: '',
      items: [
        'Other resources may include materials directly related to RCN trainings such as supplies, books, “toolkits,” materials to create visuals or self-management systems, or Peer to Peer items.',
        <>Check the <a style={{ color: 'currentcolor' }} target='_blank' href='https://docs.google.com/document/d/1Ojt5fxeKvvCot5mERZaGGqihSdYFdoMw422SDIQkyFM/edit?tab=t.0'>RCN Contract Spending Guidelines</a> for allowable uses of RCN Contract funds.</>,
        'Contact the START grant manager for questions about allowable expenses.'
      ]
    }
  ]

  return <>
    {props.showTableOnly !== true && (
      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <BlueButton onClick={() => { void startModal('add') }} dataTestId='addResourceButton'>
            Add Resource <AddCircle sx={{ ml: '10px' }} fontSize='small' />
          </BlueButton>
        }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='resourcesThingsToConsiderButton'
            thingsToConsiderSections={thingsToConsiderSections}
            cardDataTestId='resourcesThingsToConsiderCard'
          />
        </Box>
      </Box>
    )}

    <Box
      data-testid='resourcesList'
      sx={{
        border: '1px solid var(--blue-300)',
        backgroundColor: 'var(--white)',
        borderRadius: '8px',
        padding: resources.length === 0 ? '24px' : '0',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 10px 10px -8px #3B353E4D'
      }}
    >
      {isLoading
        ? <CircularProgress sx={{ alignSelf: 'center' }} />
        : (resources.length === 0
            ? <>
              <CalendarViewMonth sx={{ color: 'var(--blue-300)', width: '144px', height: '144px', alignSelf: 'center' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                <Typography variant='h4'>No Resources Added</Typography>
                {!props.readOnly &&
                  <Typography variant='body1' fontSize='16px'>
                    Use the Add Resource button to add Other Professional Learning Resources to your Annual Plan.
                  </Typography>
                }
              </Box>
            </>
            : <BasicTable
                dataTestId='resourcesTable'
                columns={columns}
                rows={rows}
                isLoading={isLoading}
                readonly={props.readOnly}
                trackingReadonly={props.trackingReadonly}
                onEdit={async (resource) => { await startModal('update', resource) }}
                onDelete={async (resource) => { await startModal('delete', resource) }}
                onTrack={async (resource) => { await startModal('track', resource) }}
                onUpdate={async (resource) => { await startModal('update', resource) }}
                hideExpand={props.hideExpand}
                implementationPhase={props.implementationPhase}
                budgetOverviewView={props.budgetOverviewView}
            />)
      }

      {(modalState === 'add' || (modalState === 'update' && currentItem != null)) &&
        <ProfessionalDevelopmentResourceModal
          onClose={handleCloseModal}
          onSaved={loadResources}
          initialValues={currentItem}
          priorityId={props.priorityId}
          implementationPhase={props.implementationPhase}
        />
      }

      {modalState === 'delete' && currentItem != null &&
        <DeleteItemModal
          item={currentItem}
          deleteFunction={DeleteProfessionalDevelopmentResource}
          onClose={handleCloseModal}
          onConfirm={loadResources}
          itemType='Resource'
        />
      }

      {modalState === 'track' && currentItem != null &&
        <TrackProfessionalResourceDevelopmentResourceModal
          onClose={handleCloseModal}
          onSave={loadResources}
          resource={currentItem}
        />
      }

      {modalState === 'clearTrack' && currentItem != null &&
        <ClearTrackingModal
          onConfirm={async () => {
            await ClearProfessionalDevelopmentResourceTracking(currentItem.id)
            await loadResources()
          }}
          onClose={handleCloseModal}
        />
    }
    </Box>
  </>
}
